import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth.tsx'
import ContentProcessingTest from './ContentProcessingTest'
import NewsSourcesManagement from './NewsSourcesManagement'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/vertical-tabs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Code, Shield, Settings, Database, Users, Calendar, FileText, Activity, MessageSquare, TestTube, AlertTriangle } from 'lucide-react'
import NewsContentsView from './NewsContentsView'
import CronLogsCleanFrontend from './CronLogsCleanFrontend'
import LlmUsageDashboard from './LlmUsageDashboard'
import LlmLogs from './LlmLogs'
import MarketingMessagesManagement from './MarketingMessagesManagement'
import TestFunctions from './TestFunctions'
import ErrorLogsManagement from './ErrorLogsManagement'
import AccountsManagement from './AccountsManagement'

export default function AdminConsole() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const userRoles = user?.user_metadata?.roles || []
    const loading = false; // temp to get compiling working

    useEffect(() => {
        if (!loading && !userRoles.includes('admin') && !userRoles.includes('superadmin')) {
            console.log('Not an admin, redirecting to /')
            // navigate('/') // disabling for now
        }
    }, [userRoles, navigate, loading])


    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30">
            {/* Enhanced Admin Console Header */}
            <div className="px-4 sm:px-6 lg:px-8 pt-8">
                <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-8">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-200/20 to-transparent rounded-full translate-y-16 -translate-x-16" />
                    <div className="relative">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                                <Shield className="h-8 w-8 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">
                                    Superadmin Console
                                </h1>
                                <p className="text-gray-600 text-lg leading-relaxed">
                                    Manage system operations, monitor performance, and oversee platform functionality with advanced administrative controls.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                <Tabs defaultValue="accounts" className="flex gap-6" asChild={false}>
                    <TabsList className="w-64 bg-gradient-to-b from-white/90 to-slate-50/90 backdrop-blur-sm border border-slate-200/50 shadow-lg rounded-xl p-3 h-fit">
                    <TabsTrigger value="accounts" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <Users className="h-4 w-4" />
                        <span className="font-medium">Accounts</span>
                    </TabsTrigger>
                    <TabsTrigger value="scheduled-tasks" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">Scheduled Tasks</span>
                    </TabsTrigger>
                    <TabsTrigger value="content-test" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <FileText className="h-4 w-4" />
                        <span className="font-medium">Manual Content Add</span>
                    </TabsTrigger>
                    <TabsTrigger value="news-sources" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <Database className="h-4 w-4" />
                        <span className="font-medium">News Sources</span>
                    </TabsTrigger>
                    <TabsTrigger value="news-contents" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <FileText className="h-4 w-4" />
                        <span className="font-medium">News Contents</span>
                    </TabsTrigger>
                    <TabsTrigger value="llm-usage" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <Activity className="h-4 w-4" />
                        <span className="font-medium">LLM Usage</span>
                    </TabsTrigger>
                    <TabsTrigger value="llm-logs" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <FileText className="h-4 w-4" />
                        <span className="font-medium">LLM Logs</span>
                    </TabsTrigger>
                    <TabsTrigger value="marketing-messages" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <MessageSquare className="h-4 w-4" />
                        <span className="font-medium">Marketing Messages</span>
                    </TabsTrigger>
                    <TabsTrigger value="test-functions" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <TestTube className="h-4 w-4" />
                        <span className="font-medium">Test Functions</span>
                    </TabsTrigger>
                    <TabsTrigger value="error-logs" className="flex items-center gap-3 py-3 px-4 transition-all duration-200 hover:bg-blue-50/50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-100 data-[state=active]:to-indigo-100 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm rounded-lg">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">Error Logs & Alerts</span>
                    </TabsTrigger>
                </TabsList>

                <div className="flex-grow">
                    <TabsContent value="accounts" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <Users className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        Account Management
                                    </h2>
                                </div>
                                <AccountsManagement />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="convergence" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <Settings className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        Convergence Control
                                    </h2>
                                </div>
                                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-lg p-4">
                                    <p className="text-sm text-amber-800 leading-relaxed">
                                        Convergence management functionality has been moved to automated processes. 
                                        Manual triggers are no longer available in this interface.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="scheduled-tasks" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <Calendar className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        Scheduled Tasks
                                    </h2>
                                </div>
                                <div className="space-y-8">
                                    <CronLogsCleanFrontend />
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="content-test" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <FileText className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        Manual Content Add
                                    </h2>
                                </div>
                                <Alert className="mb-6 bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200/50 shadow-sm">
                                    <Code className="h-4 w-4 text-slate-600" />
                                    <AlertDescription className="font-mono text-xs mt-2 text-slate-700">
                                        To manually add content for a given source:<br />
                                        curl -X POST "{import.meta.env.VITE_WORKERS_URL}/rainfactory/add-content-by-source?source=Coverager&date=2024-02-13T00:00:00Z" -H "X-API-Key: uInnbPUejxAe+yXAifFBmvvTHhjHPZXY1DlXW8pOx50=" -H "Content-Type: application/json" --data-binary @sample_content.md
                                    </AlertDescription>
                                </Alert>
                                <ContentProcessingTest />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="news-sources" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <Database className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        News Sources
                                    </h2>
                                </div>
                                <NewsSourcesManagement />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="news-contents" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <FileText className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        News Contents
                                    </h2>
                                </div>
                                <NewsContentsView />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="llm-usage" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <Activity className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        LLM Usage Dashboard
                                    </h2>
                                </div>
                                <LlmUsageDashboard />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="llm-logs" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <FileText className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        LLM Logs
                                    </h2>
                                </div>
                                <LlmLogs />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="marketing-messages" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <MessageSquare className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        Marketing Messages
                                    </h2>
                                </div>
                                <MarketingMessagesManagement />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="test-functions" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-100/80 rounded-lg">
                                        <TestTube className="h-5 w-5 text-blue-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                        Test Functions
                                    </h2>
                                </div>
                                <TestFunctions />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="error-logs" className="mt-0">
                        <div className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 rounded-xl border border-slate-200/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-blue-100/10" />
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-red-100/80 rounded-lg">
                                        <AlertTriangle className="h-5 w-5 text-red-700" />
                                    </div>
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-red-700 to-orange-700 bg-clip-text text-transparent">
                                        Error Logs & Alerts
                                    </h2>
                                </div>
                                <ErrorLogsManagement />
                            </div>
                        </div>
                    </TabsContent>
                </div>
            </Tabs>
            </div>
        </div>
    )
} 
